import { useMemo, useCallback } from 'react'
import { dashboardv99990101Client } from '@sequencehq/api/dashboard/v99990101'
import { useSequenceInfiniteQuery } from '@sequencehq/api'
import { TableRow, resolveRowType } from './groupingEngine'
import type {
  RevenueWaterfallQueryParams,
  RevenueWaterfallRow,
  RevenueWaterfallTotals,
  YearMonth
} from './types'

const PAGE_SIZE = 50

const sortYearMonths = (a: YearMonth, b: YearMonth) => {
  const [aYear, aMonth] = a.split('-').map(Number)
  const [bYear, bMonth] = b.split('-').map(Number)

  if (aYear === bYear) {
    return aMonth - bMonth
  }

  return aYear - bYear
}

type TableRowBuildContext = {
  monthlyColumns: Set<YearMonth>
  flatRows: TableRow[]
}

const mapApiRowToTableRow = (
  apiRow: RevenueWaterfallRow,
  path: number[],
  depth: number,
  parentInternalId: string | undefined,
  ctx: TableRowBuildContext
): TableRow => {
  const internalId = path.join('.')

  const monthlyValues = Object.entries(apiRow.monthlyColumns ?? {}).reduce<
    Record<YearMonth, number>
  >((acc, [yearMonth, value]) => {
    const key = yearMonth as YearMonth
    ctx.monthlyColumns.add(key)
    acc[key] = typeof value === 'number' ? value : Number(value)
    return acc
  }, {})

  const type = resolveRowType(apiRow.groupType)

  const row: TableRow = {
    internalId,
    parentInternalId,
    depth,
    id: apiRow.id,
    label: apiRow.label,
    type,
    customerId: type === 'customer' ? apiRow.id : undefined,
    productId: type === 'product' ? apiRow.id : undefined,
    bookedTotal: apiRow.bookedTotal,
    recognized: apiRow.recognized,
    remaining: apiRow.remaining,
    monthlyValues,
    children: []
  }

  ctx.flatRows.push(row)

  if (apiRow.children?.length) {
    row.children = apiRow.children.map((child, index) =>
      mapApiRowToTableRow(child, [...path, index], depth + 1, internalId, ctx)
    )
  }

  return row
}

const buildTableRows = (items: RevenueWaterfallRow[]) => {
  const ctx: TableRowBuildContext = {
    monthlyColumns: new Set<YearMonth>(),
    flatRows: []
  }

  const rows = items.map((item, index) =>
    mapApiRowToTableRow(item, [index], 0, undefined, ctx)
  )

  const monthlyColumns = Array.from(ctx.monthlyColumns).sort(sortYearMonths)

  return {
    rows,
    flatRows: ctx.flatRows,
    monthlyColumns
  }
}

type WaterfallTotals = {
  bookedTotal: number
  recognized: number
  remaining: number
  monthlyTotals: Record<YearMonth, number>
}

const extractTotals = (
  apiTotals: RevenueWaterfallTotals | null,
  monthlyColumns: YearMonth[]
): WaterfallTotals => {
  if (!apiTotals) {
    const zeroTotals = monthlyColumns.reduce<Record<YearMonth, number>>(
      (acc, month) => {
        acc[month] = 0
        return acc
      },
      {}
    )

    return {
      bookedTotal: 0,
      recognized: 0,
      remaining: 0,
      monthlyTotals: zeroTotals
    }
  }

  const monthlyTotals = Object.entries(apiTotals.monthlyColumns ?? {}).reduce<
    Record<YearMonth, number>
  >((acc, [yearMonth, value]) => {
    const key = yearMonth as YearMonth
    acc[key] = typeof value === 'number' ? value : Number(value)
    return acc
  }, {})

  // Ensure every visible month has an entry
  monthlyColumns.forEach(month => {
    if (!(month in monthlyTotals)) {
      monthlyTotals[month] = 0
    }
  })

  return {
    bookedTotal: apiTotals.bookedTotal ?? 0,
    recognized: (apiTotals as any).recognized ?? apiTotals.recognized ?? 0,
    remaining: apiTotals.remaining ?? 0,
    monthlyTotals
  }
}

type PaginatedWaterfallQueryResult = {
  rows: TableRow[]
  flatRows: TableRow[]
  monthlyColumns: YearMonth[]
  totals: WaterfallTotals
  infiniteQuery: ReturnType<typeof useSequenceInfiniteQuery>['infiniteQuery']
  hasNextPage: boolean
  isFetchingNextPage: boolean
  fetchNextPage: () => void
}

const usePaginatedWaterfallQuery = ({
  bookedFrom,
  bookedTo,
  recognizedUpTo,
  currency,
  customerId,
  productId,
  groupBy,
  subGroupBy,
  sortBy,
  sortDirection
}: RevenueWaterfallQueryParams): PaginatedWaterfallQueryResult => {
  const { infiniteQuery } = useSequenceInfiniteQuery(
    dashboardv99990101Client.getRevenueWaterfallData,
    {
      bookedFrom,
      bookedTo,
      recognizedUpTo,
      currency,
      groupBy,
      subGroupBy,
      sortBy,
      sortDirection,
      productId,
      customerId,
      limit: PAGE_SIZE
    },
    {
      placeholderData: previous => previous
    }
  )

  const pages = infiniteQuery.data?.pages ?? []

  const { rows, flatRows, monthlyColumns } = useMemo(() => {
    if (!pages.length) {
      return {
        rows: [] as TableRow[],
        flatRows: [] as TableRow[],
        monthlyColumns: [] as YearMonth[]
      }
    }

    const allItems: RevenueWaterfallRow[] = []

    pages.forEach(page => {
      if (page?.items?.length) {
        allItems.push(...page.items)
      }
    })

    return buildTableRows(allItems)
  }, [pages])

  const apiTotals = useMemo(() => {
    for (const page of pages) {
      if (page?.totals) {
        return page.totals
      }
    }

    return null
  }, [pages])

  const totals = useMemo(
    () => extractTotals(apiTotals, monthlyColumns),
    [apiTotals, monthlyColumns]
  )

  const fetchNextPageFn = infiniteQuery.fetchNextPage
  const hasNextPage = Boolean(infiniteQuery.hasNextPage)
  const isFetchingNextPage = Boolean(infiniteQuery.isFetchingNextPage)

  const fetchNextPage = useCallback(() => {
    if (!hasNextPage || isFetchingNextPage) {
      return
    }

    void fetchNextPageFn()
  }, [fetchNextPageFn, hasNextPage, isFetchingNextPage])

  return {
    rows,
    flatRows,
    monthlyColumns,
    totals,
    infiniteQuery,
    hasNextPage,
    isFetchingNextPage,
    fetchNextPage
  }
}

type RevenueWaterfallStatus = 'loading' | 'ready' | 'empty' | 'error'

type RevenueWaterfallMeta = {
  fetchNextPage: () => void
  hasNextPage: boolean
  isFetchingNextPage: boolean
  isFetching: boolean
  isRefetching: boolean
  refetch: () => Promise<unknown>
}

type RevenueWaterfallSuccess = {
  status: 'ready'
  data: {
    monthlyColumns: YearMonth[]
    totals: WaterfallTotals
    rows: TableRow[]
    flatRows: TableRow[]
  }
  meta: RevenueWaterfallMeta
  error: null
}

type RevenueWaterfallBaseState = {
  status: Exclude<RevenueWaterfallStatus, 'ready'>
  data: null
  meta: RevenueWaterfallMeta
  error: null | unknown
}

export type RevenueWaterfallDataResult =
  | RevenueWaterfallSuccess
  | RevenueWaterfallBaseState

export const useRevenueWaterfallData = ({
  bookedFrom,
  bookedTo,
  recognizedUpTo,
  currency,
  customerId,
  productId,
  groupBy,
  subGroupBy,
  sortBy = 'label',
  sortDirection = 'ASC'
}: RevenueWaterfallQueryParams) => {
  const paginatedQueryResult = usePaginatedWaterfallQuery({
    bookedFrom,
    bookedTo,
    recognizedUpTo,
    currency,
    customerId,
    productId,
    groupBy,
    subGroupBy,
    sortBy,
    sortDirection
  })

  const { infiniteQuery, isFetchingNextPage } = paginatedQueryResult

  const hasAnyPages = Boolean(infiniteQuery.data?.pages?.length)
  const isFetching = infiniteQuery.isFetching
  const isInitialLoading = infiniteQuery.isLoading && !hasAnyPages
  const isRefetching = isFetching && !isInitialLoading && !isFetchingNextPage

  const meta: RevenueWaterfallMeta = {
    fetchNextPage: paginatedQueryResult.fetchNextPage,
    hasNextPage: paginatedQueryResult.hasNextPage,
    isFetchingNextPage,
    isFetching,
    isRefetching,
    refetch: infiniteQuery.refetch
  }

  if (isInitialLoading) {
    return {
      status: 'loading',
      data: null,
      meta,
      error: null
    } satisfies RevenueWaterfallBaseState
  }

  if (infiniteQuery.isError) {
    return {
      status: 'error',
      data: null,
      meta,
      error: infiniteQuery.error
    } satisfies RevenueWaterfallBaseState
  }

  if (!paginatedQueryResult.flatRows.length) {
    return {
      status: 'empty',
      data: null,
      meta,
      error: null
    } satisfies RevenueWaterfallBaseState
  }

  return {
    status: 'ready',
    data: {
      monthlyColumns: paginatedQueryResult.monthlyColumns,
      totals: paginatedQueryResult.totals,
      rows: paginatedQueryResult.rows,
      flatRows: paginatedQueryResult.flatRows
    },
    meta,
    error: null
  } satisfies RevenueWaterfallSuccess
}
